/**
 * Deep-comparison utility for dirty-checking forms.
 *
 * hasDataChanged(original, current) → true if any field differs.
 * Handles primitives, nested objects, arrays (order-sensitive), null/undefined.
 * Normalises "" vs undefined vs null to be equivalent for string/optional fields.
 */

export function hasDataChanged(original: unknown, current: unknown): boolean {
  return !deepEqual(original, current);
}

function deepEqual(a: unknown, b: unknown): boolean {
  // Normalise: treat null, undefined, and '' as equivalent
  const na = normalise(a);
  const nb = normalise(b);

  if (na === nb) return true;
  if (na === null || nb === null) return false;
  if (typeof na !== typeof nb) return false;

  // Numbers
  if (typeof na === 'number' && typeof nb === 'number') {
    if (Number.isNaN(na) && Number.isNaN(nb)) return true;
    return na === nb;
  }

  // Dates
  if (na instanceof Date && nb instanceof Date) {
    return na.getTime() === nb.getTime();
  }

  // Arrays
  if (Array.isArray(na) && Array.isArray(nb)) {
    if (na.length !== nb.length) return false;
    return na.every((item, i) => deepEqual(item, nb[i]));
  }

  // Objects
  if (typeof na === 'object' && typeof nb === 'object' && na !== null && nb !== null) {
    const keysA = Object.keys(na as Record<string, unknown>);
    const keysB = Object.keys(nb as Record<string, unknown>);
    const allKeys = new Set([...keysA, ...keysB]);
    for (const key of allKeys) {
      if (!deepEqual(
        (na as Record<string, unknown>)[key],
        (nb as Record<string, unknown>)[key],
      )) return false;
    }
    return true;
  }

  return false;
}

function normalise(val: unknown): unknown {
  if (val === undefined || val === null || val === '') return null;
  // Normalise numeric strings that represent the same number
  if (typeof val === 'string') {
    const num = Number(val);
    if (!Number.isNaN(num) && String(num) === val.trim()) return num;
  }
  return val;
}
